import { Component, inject, OnInit } from '@angular/core';
import { TranslocoLocaleService } from '@ngneat/transloco-locale';
import { DetailsTableRow } from '@tcc-mono/shared/ui/details-table';
import {
  AnimalSelection,
  animalSelectionDisplayName,
  animalSelectionStatusLabel,
  animalSelectionUsesRvoPool,
  formatSelectionBirthDateRange
} from '@tcc-mono/vandijk/shared/data-access/models';
import { AnimalSelectionRepository } from '@tcc-mono/vandijk/shared/data-access/state';
import { Observable, filter, from, map, switchMap, take, tap } from 'rxjs';
import { AnimalSelectionDialogComponent } from '../../../dialogs/animal-selection-dialog/src';
import { ModalController } from '@ionic/angular';
import { AreYouSureService, LoaderService } from '@tcc-mono/shared/ui/services';
import { ActivatedRoute, Router } from '@angular/router';
import { withLoading } from '@tcc-mono/shared/utils';
import { AnimalSelectionExportDialogComponent } from '../../../dialogs/animal-selection-export-dialog/src';
import { AnimalSelectionTransportDialogComponent } from '../../../dialogs/animal-selection-transport-dialog/src';
import { TransportGroupRvoResultDialogComponent } from '@tcc-mono/vandijk/web/office/feature/transport-groups/transport-group-rvo-result-dialog';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { AnimalSelectionService } from '@tcc-mono/vandijk/shared/data-access/vandijk-api';
import { ANIMAL_SELECTION_CONFIG, AnimalSelectionConfig } from '../../../animal-selection-shell/src';

@UntilDestroy()
@Component({
  selector: 'tcc-mono-animal-selection-details',
  templateUrl: './animal-selection-details.component.html',
  styleUrls: ['./animal-selection-details.component.scss']
})
export class AnimalSelectionDetailsComponent implements OnInit {
  private readonly _repo: AnimalSelectionRepository = inject(AnimalSelectionRepository);
  private readonly _locale: TranslocoLocaleService = inject(TranslocoLocaleService);
  private readonly _modalCtrl: ModalController = inject(ModalController);
  private readonly _areYouSure: AreYouSureService = inject(AreYouSureService);
  private readonly _router: Router = inject(Router);
  private readonly _route: ActivatedRoute = inject(ActivatedRoute);
  private readonly _loading: LoaderService = inject(LoaderService);
  private readonly _service: AnimalSelectionService = inject(AnimalSelectionService);
  private readonly _config: AnimalSelectionConfig = inject(ANIMAL_SELECTION_CONFIG);

  public readonly selection$: Observable<AnimalSelection> = this._repo.activeEntity$;
  public readonly rows$: Observable<DetailsTableRow[]> = this.selection$.pipe(
    filter((selection): selection is AnimalSelection => !!selection),
    map((selection) => [
      {
        key: 'Naam',
        value: animalSelectionDisplayName(selection)
      },
      {
        key: 'Relatie oorsprong',
        value: selection.source_stable_relation_name
      },
      {
        key: 'Stal oorsprong',
        value: selection.source_stable_name
      },
      {
        key: 'Geboortedatum-range',
        value: formatSelectionBirthDateRange(selection.min_birth_date, selection.max_birth_date)
      },
      {
        key: 'Aangemaakt op',
        value: this._l(selection.created_at)
      },
      {
        key: 'Status',
        value: animalSelectionStatusLabel(selection.status)
      }
    ])
  );
  public rvoCardRows: DetailsTableRow[] = [];
  public isApp: boolean;

  public ngOnInit(): void {
    this.isApp = this._config.source === 'app';

    if (!this.isApp) {
      this.selection$
        .pipe(
          filter((selection): selection is AnimalSelection => !!selection),
          untilDestroyed(this)
        )
        .subscribe((selection) => this.loadRvoCard(selection));
    }
  }

  public editSelection(selection: AnimalSelection): void {
    from(
      this._modalCtrl.create({
        component: AnimalSelectionDialogComponent,
        componentProps: {
          selection
        }
      })
    )
      .pipe(
        take(1),
        tap(modal => modal.present()),
        switchMap(modal => modal.onDidDismiss()),
        filter((res) => !!res.data),
        switchMap(() => this._repo.getSelectionWithAnimals(selection.id, true, true, true)),
        take(1)
      )
      .subscribe();
  }

  public deleteSelection(selectionId: number): void {
    this._areYouSure
      .show()
      .pipe(
        filter(res => !!res),
        take(1),
        switchMap(() => {
          return this._repo.removeEntity(selectionId).pipe(withLoading(this._loading));
        })
      )
      .subscribe({
        next: () => this._router.navigate(['../..'], { relativeTo: this._route })
      });
  }

  public close = (selection: AnimalSelection): void => {
    this._repo
      .updateEntity(selection.id, {
        ...selection,
        closed: true
      })
      .pipe(withLoading(this._loading), take(1))
      .subscribe();
  };

  public reopen = (selection: AnimalSelection): void => {
    this._repo
      .updateEntity(selection.id, {
        ...selection,
        closed: false
      })
      .pipe(withLoading(this._loading), take(1))
      .subscribe();
  };

  public createPassports(selection: AnimalSelection): void {
    this._repo
      .createPassports(selection.id)
      .pipe(
        withLoading(this._loading),
        take(1),
        switchMap((results: { data?: unknown }) => {
          return from(
            this._modalCtrl.create({
              component: TransportGroupRvoResultDialogComponent,
              componentProps: {
                rvoResults: results.data
              }
            })
          ).pipe(tap(modal => modal.present()));
        }),
        switchMap(() => this._repo.getSelectionWithAnimals(selection.id, true, true, true))
      )
      .subscribe();
  }

  public export(selection: AnimalSelection): void {
    from(
      this._modalCtrl.create({
        component: AnimalSelectionExportDialogComponent,
        componentProps: {
          selection
        }
      })
    )
      .pipe(
        take(1),
        tap(modal => modal.present()),
        switchMap((modal) => modal.onDidDismiss()),
        switchMap(() => this._repo.getSelectionWithAnimals(selection.id, true, true, true))
      )
      .subscribe();
  }

  public transport(selection: AnimalSelection): void {
    from(
      this._modalCtrl.create({
        component: AnimalSelectionTransportDialogComponent,
        componentProps: {
          selection
        }
      })
    )
      .pipe(
        take(1),
        tap(modal => modal.present()),
        switchMap((modal) => modal.onDidDismiss()),
        switchMap(() => this._repo.getSelectionWithAnimals(selection.id, true, true, true))
      )
      .subscribe();
  }

  public downloadExcel(): void {
    this.selection$
      .pipe(
        take(1),
        switchMap(selection => this._service.getAnimalsExcel(selection.id)),
        tap((blob: Blob) => {
          const fileUrl = URL.createObjectURL(blob);
          window.open(fileUrl, '_blank');
        })
      )
      .subscribe();
  }

  public downloadExcelByColor(): void {
    this.selection$
      .pipe(
        take(1),
        switchMap(selection => this._service.getAnimalsExcelByColor(selection.id)),
        tap((blob: Blob) => {
          const fileUrl = URL.createObjectURL(blob);
          window.open(fileUrl, '_blank');
        })
      )
      .subscribe();
  }

  public downloadPdf(): void {
    this.selection$
      .pipe(
        take(1),
        switchMap(selection =>
          this._service.getAnimalsPdf(selection.id).pipe(
            map(response => ({
              blob: response.body,
              selectionId: selection.id,
              contentDisposition: response.headers.get('Content-Disposition')
            }))
          )
        ),
        tap(({ blob, selectionId, contentDisposition }: { blob: Blob, selectionId: number, contentDisposition: string | null }) => {
          let filename = `animal_selection_${selectionId}.pdf`;
          if (contentDisposition) {
            const filenameMatch = contentDisposition.match(/filename="?(.+)"?/i);
            if (filenameMatch) {
              filename = filenameMatch[1];
            }
          }

          const url = window.URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.download = filename;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          window.URL.revokeObjectURL(url);
        })
      )
      .subscribe();
  }

  private _l = (date: string): string => {
    return this._locale.localizeDate(date, this._locale.getLocale(), {
      dateStyle: 'medium',
      timeStyle: 'short'
    });
  };

  private loadRvoCard(selection: AnimalSelection) {
    this.rvoCardRows = [];
    if (!animalSelectionUsesRvoPool(selection)) {
      return;
    }
    if (selection.rvo_transport) {
      this.rvoCardRows.push(
        {
          key: 'Transportmelding op',
          value: selection.rvo_transport,
          valueType: 'date'
        },
        {
          key: selection.destination_hub_id ? 'Bestemming (VC)' : 'Bestemming (Stal)',
          value: selection.destination_hub_id
            ? selection.destination_hub_name
            : selection.destination_stable_name + ', ' + selection.destination_stable_relation_name
        },
        {
          key: 'Transportstatus',
          value:
            selection.animals?.filter(a => a.rvo_arrival_at !== null && a.rvo_departure_at !== null).length +
            ' van ' +
            selection.animals.length
        }
      );
    }
    if (selection.rvo_export) {
      this.rvoCardRows.push(
        {
          key: 'Exportmelding op',
          value: selection.rvo_export,
          valueType: 'date'
        },
        {
          key: 'Exportstatus',
          value: selection.animals?.filter(a => a.rvo_export_at !== null).length + ' van ' + selection.animals.length
        }
      );
    }
    if (selection.rvo_passport) {
      this.rvoCardRows.push(
        {
          key: 'Paspoortaanvraag op',
          value: selection.rvo_passport,
          valueType: 'date'
        },
        {
          key: 'Paspoortstatus',
          value: selection.animals?.filter(a => a.rvo_passport_at !== null).length + ' van ' + selection.animals.length
        }
      );
    }
  }
}
